// import { countTransactionTypesPerDayOfWeek } from "./transactionChartUtils";
import ExcelJS from "exceljs";

export function convertToCSV(objArray) {
  if (!Array.isArray(objArray) || objArray.length === 0) return "";

  const headers = Object.keys(objArray[0]);
  const escape = (val) => {
    let str = val == null ? "" : String(val);
    if (str.includes('"')) str = str.replace(/"/g, '""');
    return `"${str}"`;
  };

  // header row
  let csv = headers.map(escape).join(",") + "\r\n";

  // data rows
  objArray.forEach((row) => {
    const line = headers.map((key) => escape(row[key])).join(",");
    csv += line + "\r\n";
  });

  return csv;
}

// export function exportTotalTransactionsByDayCSV(
//   transactionCounts,
//   storeName,
//   range,
//   fileName = "TotalTransactionsByDay.csv",
// ) {
//   // Count transactions per day of week
//   const counts = transactionCounts;

//   // Days of week labels Sun-Sat
//   const days = [
//     "Sunday",
//     "Monday",
//     "Tuesday",
//     "Wednesday",
//     "Thursday",
//     "Friday",
//     "Saturday",
//   ];

//   // Build CSV rows
//   const data = days.map((day, idx) => ({
//     Brand: "Angels Pizza",
//     Store: storeName || "All Stores",
//     DateRange: `${range.tran_date.$lte.split("T")[0]} ${
//       range.tran_date.$gte.split("T")[0]
//     }`,
//     Day: day,
//     DineIn: counts.DineIn[idx] || 0,
//     GrabFood: counts.GrabFood[idx] || 0,
//     TakeOut: counts.TakeOut[idx] || 0,
//     Delivery: counts.Delivery[idx] || 0,
//     DriveThru: counts.Drive[idx] || 0,
//     FoodPanda: counts.FoodPanda[idx] || 0,
//   }));

//   // Convert to CSV and download
//   const csvString = convertToCSV(data);
//   if (!csvString) {
//     console.warn("No data to export");
//     return;
//   }
//   const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
//   const link = document.createElement("a");
//   link.href = URL.createObjectURL(blob);
//   link.download = fileName;
//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
// }

export async function exportTotalTransactionsByDayCSV(
  transactionCounts,
  storeName,
  range,
  fileName = "TotalTransactionsByDay.xlsx",
) {
  const counts = transactionCounts || {};

  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const gte = range?.tran_date?.$gte ? new Date(range.tran_date.$gte) : null;
  const lte = range?.tran_date?.$lte ? new Date(range.tran_date.$lte) : null;

  const dateRangeLabel =
    gte && lte
      ? `${gte.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })} - ${lte.toLocaleDateString(
          "en-US",
          { year: "numeric", month: "long", day: "numeric" },
        )}`
      : "No date range selected";

  // Build rows with Total column
  const rows = days.map((day, idx) => {
    const DineIn = Number(counts.DineIn?.[idx] ?? 0);
    const GrabFood = Number(counts.GrabFood?.[idx] ?? 0);
    const TakeOut = Number(counts.TakeOut?.[idx] ?? 0);
    const Delivery = Number(counts.Delivery?.[idx] ?? 0);
    const DriveThru = Number(counts.Drive?.[idx] ?? 0);
    const FoodPanda = Number(counts.FoodPanda?.[idx] ?? 0);

    return {
      Brand: "Angels Pizza",
      Store: storeName || "All Stores",
      DateRange: dateRangeLabel,
      Day: day,
      DineIn,
      GrabFood,
      TakeOut,
      Delivery,
      DriveThru,
      FoodPanda,
      Total: DineIn + GrabFood + TakeOut + Delivery + DriveThru + FoodPanda,
    };
  });

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Analytics Export";
  workbook.created = new Date();

  const sheet = workbook.addWorksheet("Transactions by Day", {
    views: [{ state: "frozen", ySplit: 4 }],
  });

  // ===== Title =====
  sheet.mergeCells("A1:K1");
  const titleCell = sheet.getCell("A1");
  titleCell.value = "Total Transaction vs. Day of Week";
  titleCell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
  titleCell.alignment = { vertical: "middle", horizontal: "left" };
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF1F2937" },
  };
  sheet.getRow(1).height = 26;

  // ===== Meta =====
  sheet.mergeCells("A2:K2");
  const metaCell = sheet.getCell("A2");
  metaCell.value = `Brand: Angels Pizza   |   Store: ${
    storeName || "All Stores"
  }   |   Date Range: ${dateRangeLabel}`;
  metaCell.font = { italic: true, size: 11, color: { argb: "FF111827" } };
  metaCell.alignment = { vertical: "middle", horizontal: "left" };
  metaCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3F4F6" },
  };
  sheet.getRow(2).height = 18;

  sheet.addRow([]);
  sheet.getRow(3).height = 6;

  // ===== Header + Data =====
  const headers = [
    "Brand",
    "Store",
    "Date Range",
    "Day",
    "Dine In",
    "GrabFood",
    "Take Out",
    "Delivery",
    "Drive Thru",
    "FoodPanda",
    "Total",
  ];
  sheet.addRow(headers);

  rows.forEach((r) => {
    sheet.addRow([
      r.Brand,
      r.Store,
      r.DateRange,
      r.Day,
      r.DineIn,
      r.GrabFood,
      r.TakeOut,
      r.Delivery,
      r.DriveThru,
      r.FoodPanda,
      r.Total,
    ]);
  });

  // Column widths
  sheet.columns = [
    { width: 16 },
    { width: 18 },
    { width: 34 },
    { width: 14 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 12 },
    { width: 10 },
  ];

  const headerRowIndex = 4;
  const firstDataRow = headerRowIndex + 1;
  const lastDataRow = headerRowIndex + rows.length;

  // Style header
  const headerRow = sheet.getRow(headerRowIndex);
  headerRow.height = 18;
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF2563EB" },
    };
    cell.border = {
      top: { style: "thin", color: { argb: "FFE5E7EB" } },
      left: { style: "thin", color: { argb: "FFE5E7EB" } },
      bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
      right: { style: "thin", color: { argb: "FFE5E7EB" } },
    };
  });

  // Style body with stripes + numeric formatting
  for (let r = firstDataRow; r <= lastDataRow; r++) {
    const row = sheet.getRow(r);
    const isAlt = (r - firstDataRow) % 2 === 1;

    row.eachCell((cell, colNumber) => {
      cell.border = {
        top: { style: "thin", color: { argb: "FFE5E7EB" } },
        left: { style: "thin", color: { argb: "FFE5E7EB" } },
        bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
        right: { style: "thin", color: { argb: "FFE5E7EB" } },
      };

      if (colNumber <= 4) {
        cell.alignment = { vertical: "middle", horizontal: "left" };
      } else {
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.numFmt = "#,##0";
      }

      if (isAlt) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF9FAFB" },
        };
      }
    });
  }

  // Totals row (SUM)
  const totalsRowIndex = lastDataRow + 1;
  sheet.mergeCells(`A${totalsRowIndex}:D${totalsRowIndex}`);

  const totalLabelCell = sheet.getCell(`A${totalsRowIndex}`);
  totalLabelCell.value = "TOTAL";
  totalLabelCell.font = { bold: true };
  totalLabelCell.alignment = { vertical: "middle", horizontal: "left" };
  totalLabelCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFEFF6FF" },
  };

  // Sum E..K
  ["E", "F", "G", "H", "I", "J", "K"].forEach((col) => {
    const cell = sheet.getCell(`${col}${totalsRowIndex}`);
    cell.value = { formula: `SUM(${col}${firstDataRow}:${col}${lastDataRow})` };
    cell.font = { bold: true };
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.numFmt = "#,##0";
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFEFF6FF" },
    };
    cell.border = {
      top: { style: "thin", color: { argb: "FF93C5FD" } },
      left: { style: "thin", color: { argb: "FF93C5FD" } },
      bottom: { style: "thin", color: { argb: "FF93C5FD" } },
      right: { style: "thin", color: { argb: "FF93C5FD" } },
    };
  });

  // Filter
  sheet.autoFilter = {
    from: { row: headerRowIndex, column: 1 },
    to: { row: headerRowIndex, column: headers.length },
  };

  // ===== Download (no file-saver) =====
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(link.href), 500);
}
